import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import useRouteMeta from '../../../hooks/useRouteMeta';
import './PricingPage.css';
import '../PricingNew/PricingNew.css';
import FlutterServicesAPI from '../../../services/flutterServices';

const PricingPage = () => {
  // SEO Meta Tags - Automatically applied from config
  useRouteMeta();
  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const subscriptionPlans = [
    {
        id: 'lite',
        name: 'Lite',
        monthlyPrice: '₹ 999/month',
        monthlyCredits: '1000 Tokens',
        yearlyPrice: '₹ 9,999/year',
        yearlyCredits: '12000 Tokens',
        discount: '20% More',
        popular: false,
        bestValue: false,
        borderColor: '#1E4966',
        topBorderColor: '#1E4966',
        discountBgColor: '#1E4966',
        innerBorderColor: '#d6d3e1',
        checkmarkColor: '#1E4966',
        features: [
            'Bulk Upload',
            'PDF Catalogue Downloads',
            'Web Catalogues',
        ],
    },
    {
        id: 'growth',
        name: 'Growth',
        monthlyPrice: '₹ 2,499/month',
        monthlyCredits: '2500 Tokens',
        yearlyPrice: '₹ 24,999/year',
        yearlyCredits: '30,000 Tokens',
        discount: '20% More',
        popular: false,
        bestValue: false,
        borderColor: '#882DC0',
        topBorderColor: '#882DC0',
        discountBgColor: '#882DC0',
        innerBorderColor: '#882DC0',
        checkmarkColor: '#882DC0',
        features: [
            'Bulk Upload',
            'PDF Catalogue Downloads',
            'Web Catalogues',
            'Reels',
            'AI Model Studio',
        ],
    },
    {
        id: 'pro',
        name: 'Pro',
        monthlyPrice: '₹ 6,999/month',
        monthlyCredits: '7000 Tokens',
        yearlyPrice: '₹ 69,999/year',
        yearlyCredits: '84,000 Tokens',
        discount: '20% More',
        popular: true,
        bestValue: false,
        borderColor: '#D9627F',
        topBorderColor: '#D9627F',
        discountBgColor: '#D9627F',
        innerBorderColor: '#D9627F',
        checkmarkColor: '#D9627F',
        features: [
            'Bulk Upload',
            'PDF Catalogue Downloads',
            'Web Catalogues',
            'Reels',
            'AI Model Studio',
        ],
    },
    {
        id: 'enterprise',
        name: 'Enterprise',
        monthlyPrice: 'Contact Us',
        monthlyCredits: '',
        yearlyPrice: '',
        yearlyCredits: '',
        discount: '',
        popular: false,
        bestValue: true,
        borderColor: '#F5CA02',
        topBorderColor: '#F5CA02',
        discountBgColor: '#F5CA02',
        innerBorderColor: '#F5CA02',
        checkmarkColor: '#F5CA02',
        features: [
            'Bulk Upload',
            'PDF Catalogue Downloads',
            'Web Catalogues',
            'Reels',
            'API',
            'Integration with other CRMs/Partners',
        ],
    },
  ];

  const photoCreationPricing = [
    { contentType: 'Bust Image', standard1k: '₹20', standard2k: '₹35', standard4k: '₹80' },
    { contentType: 'Human Image', standard1k: '₹40', standard2k: '₹50', standard4k: '₹150' },
    { contentType: 'Bust Video', standard1k: '—', standard2k: '₹225', standard4k: '₹750' },
    { contentType: 'Human Video', standard1k: '—', standard2k: '₹325', standard4k: '₹1,200' },
    { contentType: 'Poster / Social Image', standard1k: '—', standard2k: '₹150', standard4k: '₹300' },
    { contentType: 'Product Photoshoot w/ Props', standard1k: '₹30', standard2k: '₹40', standard4k: '₹300' },
    { contentType: 'Plain White Product Photos', standard1k: '₹20', standard2k: '₹35', standard4k: '₹80' },
    { contentType: 'Reel Creation', standard1k: '', standard2k: '₹50', standard4k: '' },
  ];

  return (
    <div className="pricing-page">
      <Header />

      {/* Hero Section */}
      <div className="comman-gradient-header bg-linear-gradient-30">
        <div className="container">
          <h1>Flexible Pricing That Grows With You</h1>
          <h3>Request {FlutterServicesAPI.getCreditLabel()}. Create Magic. Pay Less as You Grow.</h3>
          <p>
            At Glammify, we believe in making stunning, AI-generated product visuals accessible,
            scalable, and cost-effective for every brand, from boutique businesses to enterprise-level players.
          </p>
        </div>
      </div>

      {/* Subscription Packages Section */}
      <section className="pn-section pn-packages">
        <div className="container">
          <div className="pn-section-header">
            <div className="pn-section-badge">Subscription Packages</div>
          </div>

          <div className="pn-plans-grid">
            {subscriptionPlans.map((plan) => (
              <div key={plan.id} className="pn-plan-card" style={{ '--border-color': plan.borderColor, '--top-border-color': plan.topBorderColor, '--accent-color': plan.borderColor, '--discount-color': plan.discountBgColor, '--checkmark-color': plan.checkmarkColor, '--inner-border-color': plan.innerBorderColor }}>
                {plan.popular && <div className="pn-badge pn-badge-popular">♥ Most Popular</div>}
                {plan.bestValue && <div className="pn-badge pn-badge-best">★ Best Value</div>}

                <h3 className="pn-plan-name">{plan.name}</h3>

                <div className="pn-pricing-section">
                  {plan.monthlyPrice === 'Contact Us' ? (
                    <button className="pn-btn pn-btn-contact">
                      <span className="btn-text">Contact Us</span>
                    </button>
                  ) : (
                    <div className="pn-pricing-box">
                      <div className="pn-price-row">
                        <span className="pn-price">{plan.monthlyPrice}</span>
                        <span className="pn-credits">{plan.monthlyCredits}</span>
                      </div>

                      <div className="pn-price-divider" />

                      <div className="pn-price-row pn-yearly">
                        <span className="pn-price">{plan.yearlyPrice}</span>
                        <span className="pn-credits">{plan.yearlyCredits}</span>
                      </div>

                      {plan.discount && (
                        <div className="d-flex justify-content-center">
                          <div className="pn-discount-badge">{plan.discount}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="pn-features">
                  <div
                    className={`${plan.name === 'Enterprise'
                        ? 'pn-addons-label2'
                        : 'pn-addons-label'
                      }`}
                  >
                    Add-Ons included
                  </div>                  <ul>
                    {plan.features.map((feature, idx) => (
                      <li key={idx} style={{ '--checkmark-color': plan.checkmarkColor }}>
                        <span className="pn-check-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="11" fill="var(--checkmark-color)" stroke="var(--checkmark-color)" strokeWidth="1.5" />
                            <path d="M7 12.5L10.2 15.5L17 8.5" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Creation Pricing Section */}
      <section className="pn-section pn-photo-pricing">
        <div className="container">
          <div className="pn-section-header">
            <div className="pn-section-badge">Photo Creation Pricing</div>
          </div>

          <div className="pn-table-responsive">
            <table className="pn-pricing-table table-bordered">
              <thead>
                <tr>
                  <th>Content Type</th>
                  <th>Standard 1K</th>
                  <th>Standard 2K</th>
                  <th>Standard 4K</th>
                </tr>
              </thead>
              <tbody>
                {photoCreationPricing.map((row, idx) => (
                  <tr key={idx}>
                    <td className="pn-content-type">{row.contentType}</td>
                    {row.contentType === 'Reel Creation' ? (
                      <td colSpan="3" className="pn-reel-price">{row.standard2k}</td>
                    ) : (
                      <>
                        <td>{row.standard1k}</td>
                        <td>{row.standard2k}</td>
                        <td>{row.standard4k}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className='sections bg-primary-light'>
        <div className="container-fluid">
          <div className='row justify-content-center'>
            <div className='col-md-12 text-center section-headings'>
              <h2 className='font-heading'> Why Our Customers Love Glammify Pricing</h2>
            </div>
          </div>
          <div className="row justify-content-center">
            <div className="col-lg-12 col-xxl-11 col-md-10 why-customer">
              <div className="info-cards-container">
                <div className="row">
                  {/* Card 1: Showcase Like a Brand Boss */}
                  <div className="col-md-6 col-lg-3 mb-4 mb-md-1 mb-lg-1 p-md-2 p-lg-3">
                    <div className="pricing-card">
                      <div className="card-icon">
                        <img src="/assets/img/website/prc-1.svg" alt="Transparent & predictable" />
                      </div>
                      <h4 className="pricing-card-title">Transparent & predictable</h4>
                      <p className="pricing-card-description">You pay only for what you use.</p>
                    </div>
                  </div>

                  {/* Card 2: No More Studio Runs */}
                  <div className="col-md-6 col-lg-3 mb-4 mb-md-1 mb-lg-1 p-md-2 p-lg-3">
                    <div className="pricing-card">
                      <div className="card-icon">
                        <img src="/assets/img/website/prc-2.svg" alt="Scales with your business" />
                      </div>
                      <h4 className="pricing-card-title">Scales with your business</h4>
                      <p className="pricing-card-description">Larger {FlutterServicesAPI.getCreditLabel().toLowerCase()} packs unlock bonus {FlutterServicesAPI.getCreditLabel().toLowerCase()} and reduce per-creative cost.</p>
                    </div>
                  </div>

                  {/* Card 3: Ready in Minutes */}
                  <div className="col-md-6 col-lg-3 mb-4 mb-md-1 mb-lg-1 p-md-2 p-lg-3">
                    <div className="pricing-card">
                      <div className="card-icon">
                        <img src="/assets/img/website/prc-3.svg" alt="Zero pressure" />
                      </div>
                      <h4 className="pricing-card-title">Zero pressure</h4>
                      <p className="pricing-card-description">Start small, upgrade anytime as your needs grow.</p>
                    </div>
                  </div>

                  {/* Card 4: Perfect Everywhere */}
                  <div className="col-md-6 col-lg-3 mb-4 mb-md-1 mb-lg-1 p-md-2 p-lg-3">
                    <div className="pricing-card">
                      <div className="card-icon">
                        <img src="/assets/img/website/prc-4.svg" alt="Full control" />
                      </div>
                      <h4 className="pricing-card-title">Full control</h4>
                      <p className="pricing-card-description">Use your {FlutterServicesAPI.getCreditLabel().toLowerCase()} the way you want — image, video, mannequin, model — your choice.</p>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className='sections bg-white'>
        <div className="container-fluid">
          <div className='row justify-content-center'>
            <div className='col-md-12 text-center section-headings'>
              <h2 className='font-heading'> Let’s Get You Started</h2>
            </div>
          </div>
          <div className="row justify-content-center">
            <div className="col-lg-9 why-customer">
              <p className='details-3 text-center mb-lg-4'>Buying {FlutterServicesAPI.getCreditLabel().toLowerCase()} is easy!<br /> Just reach out to us and we’ll guide you through the process:</p>
              <div className='d-lg-flex d-md-flex d-block align-items-center justify-content-center gap-3'>
                <div className='d-flex align-items-center justify-content-center justify-content-md-start justify-contentlg-start ct-details gap-2 mb-3'>
                  <img src='/assets/img/website/message.svg' alt='email' />
                  <p><a href='mailto:sales@glammify.ai'>sales@glammify.ai</a></p>
                </div>
                <div className='d-flex align-items-center justify-content-center justify-content-md-start justify-contentlg-start ct-details gap-2 mb-3'>
                  <img src='/assets/img/website/whatsapp.svg' alt='phone' />
                  <p><a href='tel:+91 7039796526'>+91 7039796526</a></p>
                </div>
              </div>
            </div>
          </div>
          <div className="row justify-content-center">
            <div className="col-lg-11 large-card">
              <div className="pricing-card-2">
                <h4 className="pricing-card-2-heading">
                  <span>
                    <img src='/assets/img/website/sparking.svg' alt='coming soon' className='sparkling-1' />
                    Coming Soon
                    <img src='/assets/img/website/sparking.svg' alt='coming soon' className='sparkling-2' />
                  </span>
                </h4>
                <p className="pricing-card-2-title">You’ll soon be able to purchase {FlutterServicesAPI.getCreditLabel().toLowerCase()} directly from your dashboard with online payment — fast, safe, and hassle-free!</p>
                <p className="pricing-card-2-title">Until then, we’re just one message away.</p>
                <p className="pricing-card-2-subtitle">Buy your {FlutterServicesAPI.getCreditLabel().toLowerCase()}. Relax. Let Glammify take care of the rest.</p>
              </div>
            </div>
          </div>
        </div>
      </section>


      <Footer />
    </div>
  );
};

export default PricingPage;
